import React, { useCallback, useEffect, useState } from 'react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import styled from 'styled-components';
import { Check, Clock, Circle, X, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import api from '../../services/api';
import { formatDate } from '../../utils/Utils';

// SALT OKUNUR onay zinciri durumu: hangi adımdayız, kim onayladı, kim bekliyor,
// red / revizyon gerekçesi ne.
//
// Neden GenericFormDrawer DEĞİL: bu bir form değil — ev kuralı "form açmak için
// GenericFormDrawer"dır. Çekmece kabuğu (react-modern-drawer + aynı başlık/gövde
// ölçüleri) RequisitionDetailDrawer'ın birebir aynısıdır, tasarım dili ayrışmasın diye.
//
// Çizelgenin görsel dili ApprovalFlowTimeline'ın (izin/masraf detayı) AYNISIDIR —
// aynı ikon kümesi, aynı renkler, aynı dikey çizgi. Ayrı bir bileşen olmasının tek
// sebebi durumu KİMİN hesapladığı: orada istemci `currentApprover`dan çıkarır, burada
// SUNUCU `state` alanını yazar (zincir kuralının ikinci bir kopyası olmasın diye) ve
// ek olarak "kim, ne zaman, hangi gerekçeyle" bilgisi çizilir.

const STATE_COLORS = {
  approved: { color: '#16A34A', bg: '#DCFCE7' },
  waiting: { color: '#EA580C', bg: '#FFF7ED' },
  rejected: { color: '#DC2626', bg: '#FEE2E2' },
  revision_requested: { color: '#CA8A04', bg: '#FEF9C3' },
  pending: { color: '#94A3B8', bg: '#F1F5F9' },
};

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #E8ECF0;
`;

const Title = styled.h3`
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  margin: 0;
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: none;
  color: ${({ theme }) => theme.colors.text.dark.little};
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.bgColor}; }
`;

// TEK KAYDIRMA: gövde kendi içinde kayar, sayfa kaymaz. `min-height: 0` atlanırsa
// flex çocuğu küçülmez ve taşma sayfaya döner (ev kuralı).
const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px 24px;
`;

const SectionTitle = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin: 22px 0 10px;
  &:first-child { margin-top: 0; }
`;

const Rows = styled.dl`
  margin: 0;
  display: grid;
  grid-template-columns: minmax(120px, 40%) 1fr;
  gap: 8px 16px;
`;

const Label = styled.dt`
  margin: 0;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const Value = styled.dd`
  margin: 0;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  word-break: break-word;
`;

const Notice = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  border-radius: 6px;
  padding: 12px 14px;
  white-space: pre-wrap;
  word-break: break-word;
  background: ${({ $tone }) => ($tone === 'danger' ? '#FEF2F2' : $tone === 'warn' ? '#FEFCE8' : '#F5F7F9')};
  border: 1px solid ${({ $tone }) => ($tone === 'danger' ? '#FECACA' : $tone === 'warn' ? '#FDE68A' : '#E8ECF0')};
  color: ${({ $tone }) => ($tone === 'danger' ? '#B3261E' : $tone === 'warn' ? '#854D0E' : '#475569')};
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Item = styled.div`
  display: flex;
  gap: 14px;
  align-items: stretch;
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
`;

const IconCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  border: 2px solid ${({ $color }) => $color};
`;

const Line = styled.div`
  width: 2px;
  flex: 1;
  background: ${({ $color }) => $color || '#E2E8F0'};
  min-height: 24px;
`;

const Content = styled.div`
  padding: 4px 0 24px;
  flex: 1;
`;

const StepName = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StepType = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: #94A3B8;
  margin-top: 2px;
`;

const StepState = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  margin-top: 6px;
  color: ${({ $color }) => $color};
`;

const StepNote = styled.div`
  margin-top: 8px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: #475569;
  background: #F5F7F9;
  border-radius: 6px;
  padding: 8px 10px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const Empty = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: #94A3B8;
  padding: 24px 0;
  text-align: center;
`;

function StepIcon({ state }) {
  const cfg = STATE_COLORS[state] || STATE_COLORS.pending;
  const Icon = state === 'approved' ? Check
    : state === 'rejected' ? X
      : state === 'revision_requested' ? RotateCcw
        : state === 'waiting' ? Clock
          : Circle;
  return (
    <IconCircle $color={cfg.color} $bg={cfg.bg}>
      <Icon size={16} />
    </IconCircle>
  );
}

export default function OfferApprovalChainDrawer({ open, offerId, onClose }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [chain, setChain] = useState(null);
  const [loading, setLoading] = useState(false);
  // api.js GET'lerde reject ETMEZ: 403/404/500'de de `.then` çalışır ve `data` boş gelir.
  // Bu yüzden GERÇEK sinyal `res.data.status`tur — aksi hâlde yüklenememiş bir zincir
  // ekranda "adım yok" (yani tek seviyeli onay) gibi görünürdü ve onaycı, olmayan bir
  // zincire bakıp yanlış karar verirdi.
  const [failed, setFailed] = useState(false);

  const load = useCallback(() => {
    if (!offerId) return;
    setLoading(true);
    setFailed(false);
    api.get(`/v1/ats-offers/${offerId}/approval-chain`)
      .then((res) => {
        if (res?.data?.status === true && res.data.data) {
          setChain(res.data.data);
        } else {
          setChain(null);
          setFailed(true);
        }
      })
      .catch(() => { setChain(null); setFailed(true); })
      .finally(() => setLoading(false));
  }, [offerId]);

  useEffect(() => {
    if (open) load();
    else { setChain(null); setFailed(false); }
  }, [open, load]);

  const d = (v) => (v ? formatDate(v, lang) : '-');
  const dash = (v) => (v === null || v === undefined || v === '' ? '-' : v);

  const decisionTone = chain?.status === 'approval_rejected' ? 'danger' : 'warn';
  // Sunucu `Steps`i her zaman gonderir (bos liste dahil); yine de dizi OLMAYAN bir
  // govde gelirse cekmece patlamasin — okuma yuzeyi hicbir zaman beyaz ekran vermemeli.
  const steps = Array.isArray(chain?.steps) ? chain.steps : [];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      direction="right"
      size="45%"
      duration={150}
      overlayOpacity={0.2}
      style={{
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100vw',
        minWidth: 380,
        margin: '12px',
        borderRadius: '6px',
        height: 'calc(100vh - 24px)',
        boxShadow: '0 8px 32px rgba(0, 66, 66, 0.12)',
      }}
    >
      <Header>
        <Title>{t('ats.offer.chain.title')}</Title>
        <CloseBtn onClick={onClose} aria-label={t('common.close')}><X size={20} /></CloseBtn>
      </Header>

      <Body>
        {loading && <Empty>{t('common.loading')}</Empty>}

        {!loading && failed && (
          <Notice $tone="danger">{t('ats.offer.chain.loadFailed')}</Notice>
        )}

        {!loading && !failed && chain && (
          <>
            <SectionTitle>{t('ats.offer.chain.summary')}</SectionTitle>
            <Rows>
              <Label>{t('ats.offer.statusLabel')}</Label>
              <Value>{t(`ats.offer.status.${chain.status}`, { defaultValue: chain.status })}</Value>
              {chain.hasChain ? (
                <>
                  <Label>{t('ats.offer.chain.flowName')}</Label>
                  <Value>{dash(chain.approvalFlowName)}</Value>
                  <Label>{t('ats.offer.chain.currentStep')}</Label>
                  <Value>
                    {chain.currentStepOrder != null
                      ? t('ats.offer.chain.stepOf', {
                        current: steps.findIndex((x) => x.stepOrder === chain.currentStepOrder) + 1,
                        total: steps.length,
                      })
                      : '-'}
                  </Value>
                </>
              ) : (
                <>
                  <Label>{t('ats.offer.approver')}</Label>
                  <Value>{dash(chain.approverName)}</Value>
                </>
              )}
            </Rows>

            {chain.approvalDecisionReason && (
              <>
                <SectionTitle>
                  {chain.status === 'approval_rejected'
                    ? t('ats.offer.chain.rejectedTitle')
                    : t('ats.offer.chain.revisionTitle')}
                </SectionTitle>
                <Notice $tone={decisionTone}>
                  {`${chain.approvalDecisionReason}\n${d(chain.approvalDecisionAt)}`}
                </Notice>
              </>
            )}

            <SectionTitle>{t('ats.offer.chain.steps')}</SectionTitle>

            {!chain.hasChain ? (
              <Notice>{t('ats.offer.chain.singleLevel')}</Notice>
            ) : (
              <Wrapper>
                {steps.map((step, i) => {
                  const cfg = STATE_COLORS[step.state] || STATE_COLORS.pending;
                  const isLast = i === steps.length - 1;
                  return (
                    <Item key={`${step.stepOrder}-${step.approverId}`} data-role="chain-step">
                      <Left>
                        <StepIcon state={step.state} />
                        {!isLast && (
                          <Line $color={step.state === 'approved' ? STATE_COLORS.approved.color : '#E2E8F0'} />
                        )}
                      </Left>
                      <Content>
                        <StepName>{step.approverName || step.approverCode || '—'}</StepName>
                        <StepType>
                          {`${t('approvalFlow.step')} ${i + 1} · `}
                          {step.approverType === 'person'
                            ? t('approvalFlow.approverPerson')
                            : t('approvalFlow.approverPosition')}
                        </StepType>
                        <StepState $color={cfg.color}>
                          {t(`ats.offer.chain.state.${step.state}`, { defaultValue: step.state })}
                          {step.decidedAt ? ` · ${d(step.decidedAt)}` : ''}
                          {step.decidedByName ? ` · ${step.decidedByName}` : ''}
                        </StepState>
                        {/* VEKÂLET İZİ — "kim verdi" satırın kendisinde, "kimin adına"
                            AYRI satırda. Tek satıra birleştirilirse vekilin kararı
                            verenin imzası gibi okunur; ayrık gösterim ikisini de
                            görünür tutar. Alan yalnız vekâletli kararlarda doludur. */}
                        {step.onBehalfOfName && (
                          <StepNote data-role="chain-step-on-behalf">
                            {t('ats.offer.chain.onBehalfOf', { name: step.onBehalfOfName })}
                          </StepNote>
                        )}
                        {step.note && <StepNote>{step.note}</StepNote>}
                      </Content>
                    </Item>
                  );
                })}
              </Wrapper>
            )}
          </>
        )}
      </Body>
    </Drawer>
  );
}
